import fa from './locales/fa';
import en from './locales/en';

export const LANGUAGES = ['fa', 'en'];
export const DEFAULT_LANGUAGE = 'fa';

// also read by the inline script in public/index.html
const STORAGE_KEY = 'RECO_LANGUAGE';
const dictionaries = { fa, en };

const normalize = (language) =>
  LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;

const readStoredLanguage = () => {
  try {
    return normalize(localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return DEFAULT_LANGUAGE;
  }
};

let currentLanguage = readStoredLanguage();

export const getLanguage = () => currentLanguage;

export const getDirection = (language = currentLanguage) =>
  language === 'en' ? 'ltr' : 'rtl';

/***
 * sets the module-level language, caches it and updates <html dir/lang>
 * @param {'fa' | 'en'} language
 */
export const applyLanguage = (language) => {
  currentLanguage = normalize(language);
  try {
    localStorage.setItem(STORAGE_KEY, currentLanguage);
  } catch (e) {
    // storage may be unavailable (private mode); the language still applies
  }
  document.documentElement.lang = currentLanguage;
  document.documentElement.dir = getDirection(currentLanguage);
  return currentLanguage;
};

const lookup = (dictionary, key) =>
  key
    .split('.')
    .reduce(
      (node, part) => (node && node[part] !== undefined ? node[part] : null),
      dictionary
    );

/***
 * @param {String} key dotted path, e.g. 'settings.title'
 * @param {Object} [params] values for {placeholders}
 * @param {'fa' | 'en'} [language]
 * @returns {String}
 */
export const translate = (key, params, language = currentLanguage) => {
  const text =
    lookup(dictionaries[language], key) ?? lookup(dictionaries.fa, key) ?? key;
  if (!params || typeof text !== 'string') return text;
  return text.replace(/\{(\w+)\}/g, (match, name) =>
    params[name] !== undefined ? params[name] : match
  );
};

/***
 * label for backend `{ fa, en, index }` fields; `en` is an enum key, not a label
 * @param {{fa: String, en: String}} repr
 * @param {String} group key under `enums` in the locale files
 * @param {'fa' | 'en'} [language]
 */
export const reprLabel = (repr, group, language = currentLanguage) => {
  if (!repr) return '';
  if (language === 'fa') return repr.fa;
  const label = lookup(dictionaries.en, `enums.${group}.${repr.en}`);
  return label ?? repr.en;
};

export const formatNumber = (value, language = currentLanguage) =>
  Number(value).toLocaleString(language === 'en' ? 'en-US' : 'fa-IR');
